/**
 * Axios client for backend API (React app, browser only).
 * Attaches token from localStorage (key: `token`) when present.
 */

import axios from 'axios';
import { AppConfig } from '@/lib/config';

const client = axios.create({
    baseURL: AppConfig.apiBaseUrl,
    headers: { 'Content-Type': 'application/json' },
    // Increase timeout to better handle heavier endpoints (e.g. archive list).
    timeout: 120_000,
});

// Request: add Bearer token from localStorage
client.interceptors.request.use(
    (config) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (!config.headers) config.headers = {} as any;

        // When uploading files, let browser set multipart boundary automatically.
        // Keeping global application/json here turns FormData into `{}` payload.
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            delete (config.headers as any)['Content-Type'];
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const hasAuth = Boolean((config.headers as any).Authorization);

        if (!hasAuth) {
            // Try new token key first, fallback to legacy
            const token = localStorage.getItem('access_token') || localStorage.getItem('token');
            if (token) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (config.headers as any).Authorization = `Bearer ${token}`;
            }
        }

        return config;
    },
    (error: unknown) => Promise.reject(error)
);

// Response interceptor: handle token refresh and normalize error messages
client.interceptors.response.use(
    (res) => res,
    async (err) => {
        const originalRequest = err.config;

        // Handle 401 Unauthorized - try to refresh token
        if (err.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const refreshToken = localStorage.getItem('refresh_token');
                if (refreshToken) {
                    // Create a temporary axios instance without interceptors to avoid infinite loop
                    const refreshClient = axios.create({
                        baseURL: AppConfig.apiBaseUrl,
                        headers: { 'Content-Type': 'application/json' },
                    });
                    const response = await refreshClient.post<{ access: string }>(
                        '/api/users/auth/token/refresh/',
                        { refresh: refreshToken }
                    );

                    const newAccessToken = response.data.access;
                    if (newAccessToken) {
                        localStorage.setItem('access_token', newAccessToken);
                        // Retry original request with new token
                        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                        return client(originalRequest);
                    }
                }
            } catch (refreshError) {
                // Refresh failed, clear tokens and redirect to login
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                localStorage.removeItem('token');
                if (typeof window !== 'undefined') {
                    window.location.href = '/auth';
                }
                return Promise.reject(refreshError);
            }
        }

        // No HTTP response (timeout, network error, CORS, etc.)
        if (!err.response) {
            const code = err.code as string | undefined;
            const message =
                code === 'ECONNABORTED'
                    ? 'Request timed out. Please try again.'
                    : 'Network error. Please check your connection and try again.';
            return Promise.reject(new Error(message));
        }

        const data = (err.response.data || {}) as Record<string, unknown>;

        const pick = <T extends string[]>(...keys: T) => {
            for (const k of keys) {
                const v = data[k as keyof typeof data];
                if (!v) continue;
                if (typeof v === 'string') return v;
                if (Array.isArray(v) && v.length) return v[0];
                if (typeof v === 'object') {
                    const sub =
                        (v as Record<string, unknown>).non_field_errors ||
                        (v as Record<string, unknown>).detail ||
                        null;
                    if (sub) return Array.isArray(sub) ? sub[0] : sub;
                }
            }
            return null;
        };

        const message =
            pick('error', 'errors', 'detail', 'message', 'exception') ||
            `Request failed: ${err.response.status} ${err.response.statusText}`;

        console.error('API Error (Client):', {
            status: err.response.status,
            message,
            url: err.config?.url,
        });

        const e = new Error(message);
        (e as { response?: unknown }).response = err.response;
        return Promise.reject(e);
    }
);

export const axiosClient = client;
